import type {
  Conflict,
  LinkID,
  NormalizedLinkID,
  NormalizedTaskID,
  ScheduleReason,
  Snapshot,
  TaskID
} from "./types";

export function normalizeTaskId(id: TaskID): NormalizedTaskID {
  return String(id);
}

export function normalizeLinkId(id: LinkID): NormalizedLinkID {
  return String(id);
}

export function normalizeTaskIdSet(ids: ReadonlySet<TaskID> | undefined): ReadonlySet<NormalizedTaskID> | undefined {
  if (!ids) return undefined;
  const result = new Set<NormalizedTaskID>();
  for (const id of ids) result.add(normalizeTaskId(id));
  return result;
}

export function taskIdLookupCandidates(id: TaskID): readonly TaskID[] {
  if (typeof id === "number") return [id, String(id)];
  const numeric = Number(id);
  if (!Number.isNaN(numeric) && String(numeric) === id) return [id, numeric];
  return [id];
}

export function rawTaskId(snapshot: Snapshot, id: NormalizedTaskID): TaskID {
  return snapshot.tasks.get(id)?.rawId ?? id;
}

export function rawLinkId(snapshot: Snapshot, id: NormalizedLinkID): LinkID {
  for (const link of snapshot.links) {
    if (link.id === id) return link.rawId;
  }
  return id;
}

export function toPublicScheduleReason(snapshot: Snapshot, reason: ScheduleReason): ScheduleReason {
  switch (reason.kind) {
    case "asap":
      return {
        kind: "asap",
        linkId: rawLinkId(snapshot, normalizeLinkId(reason.linkId)),
        sourceId: rawTaskId(snapshot, normalizeTaskId(reason.sourceId))
      };
    case "alap":
      return {
        kind: "alap",
        linkId: rawLinkId(snapshot, normalizeLinkId(reason.linkId)),
        targetId: rawTaskId(snapshot, normalizeTaskId(reason.targetId))
      };
    case "summary-translation":
      return {
        kind: "summary-translation",
        summaryId: rawTaskId(snapshot, normalizeTaskId(reason.summaryId))
      };
    case "constraint":
    case "preferred":
      return reason;
    default: {
      const _exhaustive: never = reason;
      void _exhaustive;
      return reason;
    }
  }
}

export function toPublicConflict(snapshot: Snapshot, conflict: Conflict): Conflict {
  switch (conflict.kind) {
    case "constraint-violation":
      return { ...conflict, taskId: rawTaskId(snapshot, normalizeTaskId(conflict.taskId)) };
    case "calendar-non-working":
      return { ...conflict, taskId: rawTaskId(snapshot, normalizeTaskId(conflict.taskId)) };
    case "cycle":
      return {
        ...conflict,
        taskIds: conflict.taskIds.map(id => rawTaskId(snapshot, normalizeTaskId(id))),
        linkIds: conflict.linkIds.map(id => rawLinkId(snapshot, normalizeLinkId(id)))
      };
    case "unscheduled-dependency":
      return {
        ...conflict,
        taskId: rawTaskId(snapshot, normalizeTaskId(conflict.taskId)),
        blockedBy: rawTaskId(snapshot, normalizeTaskId(conflict.blockedBy))
      };
    default: {
      const _exhaustive: never = conflict;
      void _exhaustive;
      return conflict;
    }
  }
}
